/* eslint-disable import/prefer-default-export */
import { CONSTANT } from '../../../../utils/constant';

import Province from './fields/province';
import MultiProvince from './fields/multiProvince';
import Channel from './fields/channel';
import MultiChannel from './fields/multiChannel';
import Cpms from './fields/cmps';
import Outlet from './fields/outlet';
import Xid from './fields/xid';
import ChannelOrgCode from './fields/channelOrgCode';
import Dealership from './fields/dealership';
import StoreCode from './fields/storeCode';
import SalesManager from './fields/salesManager';
import SalesManagerEmail from './fields/salesManagerEmail';
import OrderAccess from './fields/orderAccess';

const {
  KEYS: {
    ROLES: {
      SALES_MANAGER,
      SALES_REP,
      SALES_SUPPORT,
      ADMIN,
      ORDER_FULFILLMENT,
      PROPOSAL_HOUSE,
      ADMIN_TIME_TRACKING,
      MANAGER_TIME_TRACKING,
      TEAM_MEMBER_TIME_TRACKING
    },
    CHANNELS: { DEALER, CORP_STORE, BCX_CARE, ADT, HEALTH, CSD, NBD }
  }
} = CONSTANT;

export const getFields = fieldData => {
  /**
   * FOR ADMIN_TIME_TRACKING, MANAGER_TIME_TRACKING, TEAM_MEMBER_TIME_TRACKING
   * SHOW
   * XID, PROVINCE
   */
  if (
    [
      ADMIN_TIME_TRACKING,
      MANAGER_TIME_TRACKING,
      TEAM_MEMBER_TIME_TRACKING
    ].includes(fieldData.role)
  ) {
    return [Province, Xid];
  }

  /**
   * FOR ADMIN, ORDER_FULFILLMENT, PROPOSAL_HOUSE
   * SHOW
   * XID, PROVINCE, MANAGER, MANAGER EMAIL
   */
  if ([ADMIN, ORDER_FULFILLMENT, PROPOSAL_HOUSE].includes(fieldData.role)) {
    return [Province, Xid, SalesManager, SalesManagerEmail];
  }

  if ([SALES_REP, SALES_MANAGER].includes(fieldData.role)) {
    /**
     * FOR THE ABOVE ROLES IF CHANNELS INCLUDES DEALER
     * SHOW
     * PROVINCE, CHANNEL, DEALERSHIP, CHANNEL_ORG_CODE, CPMS, OUTLET, MANAGER, MANAGER EMAIL
     */
    if (fieldData.channels.includes(DEALER)) {
      const isSm = fieldData.role === SALES_MANAGER;
      const cpmsRequiredForSm =
        isSm && fieldData.requiresOrderAccess === 'yes';
      const includeCpms = !isSm || cpmsRequiredForSm;
      return [
        Province,
        Channel,
        Dealership,
        ChannelOrgCode,
        ...(isSm ? [OrderAccess] : []),
        ...(includeCpms ? [Cpms] : []),
        Outlet,
        SalesManager,
        SalesManagerEmail
      ];
    }

    /**
     * FOR THE ABOVE ROLES IF CHANNELS INCLUDES CORP_STORE
     * SHOW
     * PROVINCE, CHANNEL, STORE_CODE, XID, MANAGER, MANAGER EMAIL
     */
    if (fieldData.channels.includes(CORP_STORE))
      return [
        Province,
        Channel,
        StoreCode,
        fieldData.role === SALES_MANAGER ? ChannelOrgCode : Cpms,
        Outlet,
        Xid,
        SalesManager,
        SalesManagerEmail
      ];

    /**
     * FOR THE ABOVE ROLES IF CHANNELS INCLUDES EITHER [BCX_CARE, ADT, HEALTH, CSD, NBD]
     * SHOW
     * PROVINCE, CHANNEL, XID, MANAGER, MANAGER EMAIL
     */
    if (
      fieldData.channels.some(channel =>
        [BCX_CARE, ADT, HEALTH, CSD, NBD].includes(channel)
      )
    )
      return [Province, Channel, Xid, SalesManager, SalesManagerEmail];
  }

  if (fieldData.role === SALES_SUPPORT) {
    /**
     * IF ROLE SALES_SUPPORT AND CHANNEL IS CORP_STORE
     * SHOW
     * MULTI_PROVINCE, MULTI_CHANNEL, STORE_CODE, XID, MANAGER, MANAGER EMAIL
     */
    if (fieldData.channels.includes(CORP_STORE))
      return [
        MultiProvince,
        MultiChannel,
        StoreCode,
        Xid,
        SalesManager,
        SalesManagerEmail
      ];

    return [MultiProvince, MultiChannel, Xid, SalesManager, SalesManagerEmail];
  }

  return [Province, Channel, Cpms, Outlet, SalesManager, SalesManagerEmail];
};

export const getRequiredFields = (role, channels) => {
  const commonRequireFields = [
    'role',
    'firstName',
    'lastName',
    'email',
    'contactNumber',
    'provinces'
  ];
  if ([ADMIN, ORDER_FULFILLMENT, PROPOSAL_HOUSE].includes(role)) {
    return [...commonRequireFields, 'xid', 'salesManager', 'salesManagerEmail'];
  }

  if ([SALES_REP, SALES_MANAGER].includes(role)) {
    if (channels.includes(DEALER)) {
      const isSm = role === SALES_MANAGER;
      const baseFields = [
        ...commonRequireFields,
        'channels',
        'dealership',
        'channelOrgCode'
      ];
      if (isSm) {
        baseFields.push('requiresOrderAccess');
      }
      // SR keeps cpmsId always; SM only when requiresOrderAccess === 'yes'
      // is enforced server-side and via the conditional rendering — outlet
      // and manager fields remain mandatory in both cases.
      if (!isSm) baseFields.push('cpmsId');
      baseFields.push('outlet', 'salesManager', 'salesManagerEmail');
      return baseFields;
    }

    if (channels.includes(CORP_STORE))
      return [
        ...commonRequireFields,
        'channels',
        'storeCode',
        role === SALES_MANAGER ? 'channelOrgCode' : 'cpmsId',
        'outlet',
        'xid',
        'salesManager',
        'salesManagerEmail'
      ];

    if (
      channels.some(channel =>
        [BCX_CARE, ADT, HEALTH, CSD, NBD].includes(channel)
      )
    )
      return [
        ...commonRequireFields,
        'channels',
        'xid',
        'salesManager',
        'salesManagerEmail'
      ];
    return [
      ...commonRequireFields,
      'channels',
      'cpmsId',
      'outlet',
      'salesManager',
      'salesManagerEmail'
    ];
  }

  if (role === SALES_SUPPORT) {
    if (channels.includes(CORP_STORE))
      return [
        ...commonRequireFields,
        'channels',
        'storeCode',
        'xid',
        'salesManager',
        'salesManagerEmail'
      ];
    return [
      ...commonRequireFields,
      'channels',
      'xid',
      'salesManager',
      'salesManagerEmail'
    ];
  }
  return commonRequireFields;
};

// SOF-3356 T021 — payload helper for SM + Dealer updates.
// Applies FR-018 / FR-018a / FR-018b cascade clearing in the outgoing payload
// so the server receives explicit empty-string sentinels (NEVER null — CPMS
// uniqueness validator skips empty strings only). All cascades are
// frontend-defensive; the controller (T013-T015) re-applies the same logic
// inside its Sequelize transaction.
export const buildUpdatePayload = fieldData => {
  const payload = { ...fieldData };
  const channelOff = !payload.channels || !payload.channels.includes(DEALER);
  const orderAccessNotYes = payload.requiresOrderAccess !== 'yes';
  const isSm = payload.role === SALES_MANAGER;
  const isSr = payload.role === SALES_REP;

  // FR-018b — role moved off SALES_MANAGER
  if (!isSm) {
    payload.requiresOrderAccess = '';
    payload.dealershipVisibilityEnabled = false;
    if (!isSr) payload.cpmsId = '';
  }

  // FR-018 — SM but channel off DEALER
  if (isSm && channelOff) {
    payload.requiresOrderAccess = '';
    payload.cpmsId = '';
    payload.dealershipVisibilityEnabled = false;
  }

  // FR-018a — SM + DEALER but order access not 'yes'
  if (isSm && !channelOff && orderAccessNotYes) {
    payload.cpmsId = '';
  }

  return payload;
};
